// Display helpers for unit and project data.

const STRUKTURA = {
  0.5: "Garsonjera",
  1: "Jednosoban",
  1.5: "Jednoiposoban",
  2: "Dvosoban",
  2.5: "Dvoiposoban",
  3: "Trosoban",
  3.5: "Troiposoban",
  4: "Četvorosoban",
  5: "Petosoban",
};

export const STRUKTURA_OPTIONS = Object.entries(STRUKTURA)
  .map(([value, label]) => ({ value: String(Number(value)), label }))
  .sort((a, b) => Number(a.value) - Number(b.value));

const toNumber = (value) => {
  if (typeof value === "number") return value;
  const n = parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export function strukturaLabel(value) {
  const n = toNumber(value);
  if (n === null) return value ? String(value) : "";
  return STRUKTURA[n] || `${String(n).replace(".", ",")} sobe`;
}

const FLOORS = [
  ["suteren", -1, "Suteren"],
  ["prizemlje", 0, "Prizemlje"],
  ["prvi", 1, "I sprat"],
  ["drugi", 2, "II sprat"],
  ["treci", 3, "III sprat"],
  ["treći", 3, "III sprat"],
  ["cetvrti", 4, "IV sprat"],
  ["četvrti", 4, "IV sprat"],
  ["peti", 5, "V sprat"],
  ["sesti", 6, "VI sprat"],
  ["šesti", 6, "VI sprat"],
];

export const FLOOR_OPTIONS = [
  "prizemlje",
  "prvi",
  "drugi",
  "treći",
  "četvrti",
  "peti",
  "peti potkrovlje",
];

// "peti potkrovlje" -> { order: 5.5, label: "Potkrovlje (V sprat)" }
export function floorInfo(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return { order: 99, label: "" };
  const attic = raw.includes("potkrovlje");
  const numeric = toNumber(raw);
  let match = FLOORS.find(([key]) => raw.startsWith(key));
  let order = match ? match[1] : numeric ?? 50;
  let label = match ? match[2] : numeric !== null ? `${numeric}. sprat` : value;
  if (attic) {
    if (!match && numeric === null) return { order: 60, label: "Potkrovlje" };
    return { order: order + 0.5, label: `Potkrovlje (${label.replace(" sprat", "")})` };
  }
  return { order, label };
}

export const floorLabel = (value) => floorInfo(value).label;

export function areaLabel(value) {
  const n = toNumber(value);
  if (n === null) return "";
  return `${n.toLocaleString("sr-RS", { maximumFractionDigits: 2 })} m²`;
}

export const areaNumber = (value) => toNumber(value) ?? 0;

export const STATUS = {
  slobodan: { label: "Slobodan", tone: "success" },
  rezervisan: { label: "Rezervisan", tone: "warning" },
  prodat: { label: "Prodat", tone: "muted" },
};

export const STATUS_OPTIONS = Object.entries(STATUS).map(([value, s]) => ({
  value,
  label: s.label,
}));

export const unitStatus = (unit) =>
  STATUS[unit?.status] ? unit.status : "slobodan";

export const UNIT_TYPES = [
  { value: "stan", label: "Stan" },
  { value: "lokal", label: "Lokal" },
  { value: "garaža", label: "Garažno mesto" },
];

export function unitTypeLabel(tip) {
  const t = String(tip || "stan").toLowerCase();
  return UNIT_TYPES.find((u) => u.value === t)?.label || tip;
}

// Price is stored as a number (EUR) or free text; format it for display.
export function priceLabel(value) {
  if (value === undefined || value === null || value === "") return "";
  const n = toNumber(value);
  if (n !== null && String(value).trim().match(/^[\d.,\s]+$/)) {
    return `${n.toLocaleString("sr-RS", { maximumFractionDigits: 0 })} €`;
  }
  return String(value);
}

export function compareUnits(a, b) {
  const fa = floorInfo(a.sprat).order;
  const fb = floorInfo(b.sprat).order;
  if (fa !== fb) return fa - fb;
  return (toNumber(a.broj) ?? 0) - (toNumber(b.broj) ?? 0);
}

export function unitTitle(unit) {
  return `${unitTypeLabel(unit.tip)} ${unit.broj}`.trim();
}

export function unitSummary(unit, project) {
  return [
    project?.identifier || (unit.lamela ? `Lamela ${unit.lamela}` : ""),
    unitTitle(unit),
    floorLabel(unit.sprat),
    areaLabel(unit.povrsina),
    unit.tip === "stan" || !unit.tip ? strukturaLabel(unit.struktura) : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

export function formatDate(value) {
  if (!value) return "";
  const d = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("sr-RS", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/đ/g, "dj")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
